import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { UserPlus, X, ShieldAlert } from 'lucide-react';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  formName: string;
  setFormName: (val: string) => void;
  formEmail: string;
  setFormEmail: (val: string) => void;
  formPassword: string;
  setFormPassword: (val: string) => void;
  formRole: 'penulis' | 'penerbit' | 'organisasi' | 'admin';
  setFormRole: (val: 'penulis' | 'penerbit' | 'organisasi' | 'admin') => void;
  formOrg: string;
  setFormOrg: (val: string) => void;
  formPubCode: string;
  setFormPubCode: (val: string) => void;
  formError: string | null;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isPending,
  formName,
  setFormName,
  formEmail,
  setFormEmail,
  formPassword,
  setFormPassword,
  formRole,
  setFormRole,
  formOrg,
  setFormOrg,
  formPubCode,
  setFormPubCode,
  formError,
}) => {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-3xl max-w-md w-full border border-slate-200 shadow-2xl overflow-y-auto max-h-[calc(100vh-2rem)] relative"
          >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                <h3 className="font-bold text-slate-950 text-sm">Registrasi User Baru</h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-150 text-rose-700 text-xs flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Nama Lengkap
                </label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Contoh: Budhi Joko"
                  className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Alamat Email (Akun Masuk)
                </label>
                <input
                  type="email"
                  required
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="Contoh: budhi@gmail.com"
                  className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Kata Sandi Akun
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    placeholder="Masukkan atau generate sandi..."
                    className="w-full pl-3.5 pr-14 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setFormPassword(Math.random().toString(36).substring(2, 10))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-indigo-600 hover:text-indigo-800 p-1 cursor-pointer"
                  >
                    Acak
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Peran / Role Otoritas
                </label>
                <select
                  value={formRole}
                  onChange={(e) => {
                    const selected = e.target.value as 'penulis' | 'penerbit' | 'organisasi' | 'admin';
                    setFormRole(selected);
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                >
                  <option value="penulis">Penulis (Self-Publisher)</option>
                  <option value="penerbit">Penerbit (Publisher Code Resmi)</option>
                  <option value="organisasi">Organisasi / Komunitas (Literasi)</option>
                  <option value="admin">Administrator Platform</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    {formRole === 'organisasi'
                      ? 'Nama Organisasi / Komunitas'
                      : formRole === 'penerbit'
                      ? 'Nama Lembaga Penerbit Resmi'
                      : 'Nama Organisasi / Afiliasi'}
                  </label>
                  {formRole === 'penulis' && (
                    <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded">
                      Dinonaktifkan
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  disabled={formRole === 'penulis'}
                  value={formRole === 'penulis' ? '' : formOrg}
                  onChange={(e) => setFormOrg(e.target.value)}
                  placeholder={
                    formRole === 'penulis'
                      ? 'Dinonaktifkan untuk Penulis Mandiri'
                      : formRole === 'organisasi'
                      ? 'Contoh: Komunitas Penulis Nusantara'
                      : 'Contoh: CV Pustaka Indonesia'
                  }
                  className={`w-full px-3.5 py-2 rounded-xl border text-xs transition ${
                    formRole === 'penulis'
                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed select-none'
                      : 'border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 bg-white'
                  }`}
                />
              </div>

              {(formRole === 'penerbit' || formRole === 'organisasi') && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Publisher / Org Code (4-Digit Angka)
                  </label>
                  <input
                    type="text"
                    maxLength={4}
                    value={formPubCode}
                    onChange={(e) => setFormPubCode(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Kosongkan untuk auto-generate"
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-100 transition cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isPending}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold bg-slate-900 hover:bg-slate-800 text-white shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isPending ? 'Mendaftarkan...' : 'Daftarkan Akun'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
